<template>
    <header class="timer-product-list-header" :class="{ 'is-search-open': isSearchOpen }">
        <button
            type="button"
            class="timer-product-list-header__action"
            :aria-label="$t('timersproduct.list_header.back')"
            @click="$emit('back')"
        >
            <i class="i-andy-chevron-left i-andy-m" aria-hidden="true"></i>
        </button>

        <h1 v-if="!isSearchOpen" class="timer-product-list-header__title">
            {{ $t('timersproduct.list_header.title') }}
        </h1>

        <div v-else class="timer-product-list-header__search">
            <input
                :id="searchFieldId"
                ref="searchInput"
                class="timer-product-list-header__input"
                type="text"
                :value="value"
                :placeholder="$t('timersproduct.list_header.search_placeholder')"
                :aria-label="$t('timersproduct.list_header.search')"
                autocomplete="off"
                @input="onInput"
                @keydown.esc.prevent="closeSearch"
            />
            <button
                v-if="hasQuery"
                type="button"
                class="timer-product-list-header__clear"
                :aria-label="$t('timersproduct.list_header.clear')"
                @click="onClear"
            ></button>
        </div>

        <button
            type="button"
            class="timer-product-list-header__action"
            :class="{ 'is-active': isSearchOpen }"
            :aria-label="searchToggleLabel"
            :aria-expanded="isSearchOpen ? 'true' : 'false'"
            :aria-controls="isSearchOpen ? searchFieldId : null"
            @click="toggleSearch"
        >
            <i class="i-andy-search-aaaaa i-andy-m" aria-hidden="true"></i>
        </button>
    </header>
</template>

<script>
/**
 * Header del listado de productos (BUILD-7561).
 * UI de navegación: atrás + título + búsqueda.
 * La filtración vive en el padre (searchBar / cleanSearch).
 */
export default {
    name: 'TimerProductListHeader',
    props: {
        /**
         * Texto del input (debounceSearchInput del listado).
         */
        value: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            // Restaura el input si el padre ya tiene query (p. ej. al pasar de landscape a portrait).
            isSearchOpen: String(this.value || '').trim() !== '',
            searchFieldId: 'timer-product-list-search'
        }
    },
    computed: {
        hasQuery() {
            return String(this.value).trim() !== ''
        },
        searchToggleLabel() {
            return this.isSearchOpen ? this.$t('timersproduct.list_header.close_search') : this.$t('timersproduct.list_header.search')
        }
    },
    watch: {
        value(newValue) {
            if (String(newValue).trim() !== '' && !this.isSearchOpen) {
                this.isSearchOpen = true
            }
        }
    },
    methods: {
        toggleSearch() {
            if (this.isSearchOpen) {
                this.closeSearch()
                return
            }
            this.openSearch()
        },
        openSearch() {
            this.isSearchOpen = true
            this.$emit('open')
            this.focusSearchInput()
        },
        closeSearch() {
            this.isSearchOpen = false
            this.$emit('clear')
        },
        onInput(event) {
            this.$emit('search', event.target.value)
        },
        onClear() {
            this.$emit('clear')
            this.focusSearchInput()
        },
        focusSearchInput() {
            this.$nextTick(() => {
                const input = this.$refs.searchInput
                if (input && typeof input.focus === 'function') {
                    input.focus()
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.timer-product-list-header {
    @include display-flex();
    @include align-items(center);
    gap: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    flex-shrink: 0;
    margin: 0 0 8px;

    &__action {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        @include border-radius(4px);
        flex: 0 0 44px;
        width: 44px;
        height: 44px;
        padding: 8px;
        margin: 0;
        gap: 8px;
        aspect-ratio: 1 / 1;
        border: 0;
        background: var(--color-white, #fff);
        color: var(--neutro-s-40-app);
        cursor: pointer;
        box-sizing: border-box;

        i {
            line-height: 1;
        }

        &:active,
        &.is-active {
            background: $main-t90;
            color: $main-dark;
        }
    }

    &__title {
        flex: 1 1 auto;
        min-width: 0;
        margin: 0;
        padding: 0 4px;
        overflow: hidden;
        color: $neutro-s90;
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: $text-bold;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    &__search {
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
    }

    &__input {
        @include display-flex();
        @include align-items(center);
        @include background($image: img('search_neutro.svg'), $size: 18px, $position: left 12px center);
        @include border-radius(4px);
        @include font-size(ml);
        width: 100%;
        height: 44px;
        box-sizing: border-box;
        padding: 0 40px 0 40px;
        border: 0;
        border-bottom: none;
        background-color: #fff;
        color: $color-neutral-800;
        font-family: $text;
        cursor: text;

        &:focus {
            outline: none;
        }
    }

    &__clear {
        @include border-radius(4px);
        @include background($image: img('close_dark.svg'), $size: 12px, $position: center center);
        position: absolute;
        top: 5px;
        right: 6px;
        width: 33px;
        height: 33px;
        padding: 0;
        margin: 0;
        border: 0;
        background-color: $neutro-t50;
        cursor: pointer;
    }
}
</style>
